'use client';
import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import ProductTabs from '@/components/blocks/product_tabs/ProductTabs';
import Price from '@/components/price/Price';
import { useCampaign } from '@/app/CampaignContext';
import { useModal, MODAL_TYPES } from './ModalProvider';
import Link from 'next/link';

export default function InventoryModalWrapper({
  isOpen,
  onClose,
  offer,
  variant
}) {
  const modalRef = useRef(null);
  const campaign = useCampaign();
  const pricingDetailsData = campaign?.pricing_details;
  const { modalUtils, getCloseButton } = useModal();
  const settings = offer?.settings;
  const variantImage = offer?.variant?.image
    ? offer?.variant?.image
    : offer?.product?.featuredImage;
  useEffect(() => {
    if (isOpen && modalRef?.current) {
      modalRef?.current?.focus();
      // Ensure modal content starts at the top
      modalRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  // Ensure modal scrolls to top when opened
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Find the modal container with overflow-auto
      const modalContainer = modalRef.current.closest('.overflow-auto');
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
      // Also ensure the modal body itself is at top
      modalRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  if (!isOpen || !offer) return null;

  const priceWithDiscount =
    parseFloat(offer?.variant?.price?.amount || 0) -
    parseFloat(offer?.discount || 0);

  return modalUtils.createModalWrapper({
    modalType: MODAL_TYPES.inventory,
    isOpen,
    onClose,
    containerClassName: modalUtils.containerPresets.crossSell.green,
    children: (
      <div ref={modalRef} tabIndex={-1}>
        {getCloseButton(MODAL_TYPES.inventory, onClose)}
        <div className="sm:flex">
          <div className="relative mx-auto h-[135px] w-[180px] max-w-full sm:mx-0 lg:h-[165px] lg:w-[220px]">
            {variantImage?.url && (
              <Image
                src={variantImage?.url}
                width="220"
                height="165"
                sizes="(min-width: 1024px) 220px, 180px"
                alt={
                  variantImage?.altText
                    ? variantImage?.altText?.replace(/\.[^/.]+$/, '')
                    : offer?.product?.title || ''
                }
                className="h-full w-full object-cover"
              />
            )}
          </div>
          <div className="ml-0 pt-5 sm:ml-8 sm:w-[324px] sm:pt-0 lg:ml-10 lg:w-[347px]">
            <div className="h4 text-white">{offer?.product?.title}</div>
            <div className="body-s text-dawn-grey">{offer?.variant?.title}</div>
            <Price
              price={priceWithDiscount}
              compareAtPrice={offer?.variant?.compareAtPrice?.amount}
              priceClassOverride="price-r text-orange!"
              crossedPriceClassOverride="price-r-crossed text-dawn-grey!"
              variant={`inventory-modal${variant}`}
            />
            {pricingDetailsData?.price_details_link && (
              <Link
                href={pricingDetailsData?.price_details_link.href}
                className="body-s text-dawn-grey mt-1 flex w-fit grow-0 cursor-pointer items-center underline"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${pricingDetailsData?.price_details_link.title} - Link opens in a new tab`}
              >
                <span className="mr-1">
                  {pricingDetailsData?.price_details_link.title}
                </span>
                <span className="sr-only">Link opens in a new tab</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  data-name="Layer 2"
                  viewBox="0 0 12 12"
                  width="12"
                  height="12"
                  aria-hidden="true"
                >
                  <g data-name="Layer 1">
                    <path
                      d="M4.152 7.005 10.685.5M7.056.5h3.63v3.614"
                      style={{
                        fill: 'none',
                        stroke: '#a19f9b',
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round'
                      }}
                    />
                    <path
                      d="M4.674 2.345H1.5a1 1 0 0 0-1 1v6.348a1 1 0 0 0 1 1h6.348a1 1 0 0 0 1-1V6.519"
                      style={{
                        fill: 'none',
                        stroke: '#a19f9b',
                        strokeLinecap: 'round'
                      }}
                    />
                  </g>
                </svg>
              </Link>
            )}
            {settings?.productTabs && (
              <ProductTabs
                block={settings?.productTabs}
                renderContext={'bundle-inventory-modal'}
              />
            )}
          </div>
        </div>
      </div>
    )
  });
}
